<script setup>
import { computed, ref, watch } from 'vue'
import { buildStaticMapUrl } from '@/utils/mapbox/staticMap'

const props = defineProps({
  link: { type: Object, required: true },
})

const coverFailed = ref(false)
const mapFailed = ref(false)
const logoFailed = ref(false)
watch(() => props.link?.token, () => {
  coverFailed.value = mapFailed.value = logoFailed.value = false
})

const coverImage = computed(() =>
  props.link.cover_type === 'image' && props.link.cover_url && !coverFailed.value ? props.link.cover_url : null
)
const coverEmoji = computed(() =>
  !coverImage.value && props.link.cover_type === 'emoji' && props.link.project_emoji ? props.link.project_emoji : null
)
const coverMap = computed(() =>
  !coverImage.value && !coverEmoji.value && props.link.cover_boundary && !mapFailed.value
    ? buildStaticMapUrl(props.link.cover_boundary, { width: 640, height: 260 })
    : null
)
const orgInitial = computed(() => (props.link.organization_name?.trim()[0] || '?').toUpperCase())
</script>

<template>
  <div data-testid="join-hero">
    <div class="relative h-32 w-full overflow-hidden bg-gradient-to-br from-emerald-900 via-slate-800 to-sky-900">
      <img v-if="coverImage" :src="coverImage" alt="" class="h-full w-full object-cover" @error="coverFailed = true" />
      <div v-else-if="coverEmoji" class="flex h-full w-full items-center justify-center bg-muted text-6xl">{{ coverEmoji }}</div>
      <img v-else-if="coverMap" :src="coverMap" alt="" class="h-full w-full object-cover" @error="mapFailed = true" />
    </div>

    <div class="px-6">
      <div class="relative z-10 -mt-7 flex size-14 items-center justify-center overflow-hidden rounded-xl border-4 border-card bg-card shadow-sm">
        <img
          v-if="link.organization_logo && !logoFailed"
          :src="link.organization_logo"
          :alt="link.organization_name"
          class="h-full w-full object-cover"
          @error="logoFailed = true"
        />
        <span v-else class="text-lg font-semibold text-muted-foreground">{{ orgInitial }}</span>
      </div>

      <p class="mt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">{{ link.organization_name }}</p>
      <h1 class="mt-1 flex items-center gap-2 text-xl font-semibold leading-tight" data-testid="join-project-name">
        <span v-if="link.project_emoji && !coverEmoji">{{ link.project_emoji }}</span>
        <span class="min-w-0 break-words">{{ link.project_name }}</span>
      </h1>
      <p v-if="link.project_description" class="mt-2 line-clamp-3 text-sm text-muted-foreground" data-testid="join-project-description">
        {{ link.project_description }}
      </p>
    </div>
  </div>
</template>
